import React from 'react';
import '../styles/OpenScoreCard.css';
import { CareHome } from '../types';
import { trackExternalLink } from '../utils/googleAnalytics';

interface OpenScoreCardProps {
    careHome: CareHome;
    onSelect?: (careHome: CareHome) => void;
}

// Helper function to get OpenScore gradient colors based on score
const getScoreColors = (score: number): { color1: string; color2: string } => {
    if (score > 8) return { color1: "#27E757", color2: "#0DAF35" };
    if (score >= 6.5) return { color1: "#86DF11", color2: "#5CAE13" };
    if (score >= 4.5) return { color1: "#F5C60A", color2: "#DA840B" };
    if (score >= 2) return { color1: "#F7774A", color2: "#DF520C" };
    return { color1: "#F5443A", color2: "#C11626" };
};

// Helper function to format freshness into "Updated X days ago"
const formatFreshness = (days: number): string => {
    if (days < 1) return "Data last validated today";
    if (days === 1) return "Data last validated 1 day ago";
    if (days < 30) return `Data last validated ${days} days ago`;
    if (days < 365) {
        const months = Math.floor(days / 30);
        return months === 1 ? "Data last validated 1 month ago" : `Data last validated ${months} months ago`;
    }
    const years = Math.floor(days / 365);
    return years === 1 ? "Data last validated 1 year ago" : `Data last validated ${years} years ago`;
};

// Helper function to format fees into cost indicator using relative_fees
const formatCost = (relativeFees?: number): string => {
    // Use relative_fees if available (1-5 scale), otherwise fallback to default
    if (relativeFees && relativeFees >= 1 && relativeFees <= 5) {
        return "£".repeat(relativeFees);
    }
    
    // Default fallback if relative_fees is not available
    return "";
};
// Helper function to format fees to provide the background £s
const formatCostBackground = (relativeFees?: number): string => {
    // Use relative_fees if available (1-5 scale), otherwise fallback to default
    if (relativeFees && relativeFees >= 1 && relativeFees <= 5) {
        return "£".repeat(5-relativeFees);
    }
    
    // Default fallback if relative_fees is not available
    return "";
};
// Helper function to format inspection time from Days_Since_Last_Inspected_CQC
const formatInspectionTime = (days?: number): string => {
    if (!days || days < 0) return "Unknown";
    
    if (days < 30) return "1 month";
    if (days < 365) {
        const months = Math.floor(days / 30);
        return months === 1 ? "1 month" : `${months} months`;
    }
    
    const years = Math.floor(days / 365);
    const remainingDays = days % 365;
    const months = Math.floor(remainingDays / 30);
    
    let result = years === 1 ? "1 year" : `${years} years`;
    if (months > 0) {
        result += months === 1 ? ", 1 month" : `, ${months} months`;
    }
    
    return result;
};

const OpenScoreCard: React.FC<OpenScoreCardProps> = ({ careHome, onSelect }) => {
    const handleClick = () => {
        console.log('🃏 OpenScoreCard: Card clicked for:', careHome.organisation_name, 'ID:', careHome.id);
        onSelect?.(careHome);
        console.log('🃏 OpenScoreCard: onSelect called');
    };

    const scoreColors = getScoreColors(careHome.open_score);
    const updatedText = formatFreshness(careHome.freshness_days);
    const costIndicator = formatCost(careHome.relative_fees);
    const costIndicatorBackground = formatCostBackground(careHome.relative_fees);
    const inspectionTime = formatInspectionTime(careHome.Days_Since_Last_Inspected_CQC);
    
    // Debug: Log the colors being used (remove this later)
    console.log(`Score: ${careHome.open_score}, Colors: ${scoreColors.color1} -> ${scoreColors.color2}`);
    
    // Calculate stroke-dasharray for circular progress (score out of 10)
    const progressPercentage = (careHome.open_score / 10) * 100;

    // Create unique gradient ID for this card (include score to force update)
    const gradientId = `gradient-${careHome.id}-${careHome.open_score.toFixed(1).replace('.', '-')}`;

    return (
        <div 
            className="osc-card"
            onClick={handleClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleClick()}
        >
            <div className="osc-header">
                <div className="osc-header-row">
                    <h3 className="osc-org-name">{careHome.organisation_name}</h3>
                </div>
                <div className="osc-header-row">
                    <div>
                        <svg width="48" height="48" viewBox="-1 -1 38 38" className="osc-circular-chart">
                            <defs>
                                <linearGradient 
                                    id={gradientId} 
                                    gradientTransform="rotate(43.09)"
                                    key={gradientId}
                                >
                                    <stop 
                                        offset="25.83%" 
                                        stopColor={scoreColors.color1}
                                        key={`${gradientId}-stop1`}
                                    />
                                    <stop 
                                        offset="57.78%" 
                                        stopColor={scoreColors.color2}
                                        key={`${gradientId}-stop2`}
                                    />
                                </linearGradient>
                            </defs>
                            <path 
                                className="osc-circle-bg" 
                                d="M18 2.0845 a 15.9155 15.9155 0 1 1 0 31.831 a 15.9155 15.9155 0 1 1 0 -31.831" 
                            />
                            <path 
                                className="osc-circle" 
                                d="M18 2.0845 a 15.9155 15.9155 0 1 1 0 31.831 a 15.9155 15.9155 0 1 1 0 -31.831" 
                                stroke={`url(#${gradientId})`}
                                strokeDasharray={`${progressPercentage}, 100`}
                            />
                            <text x="18" y="19.5" className="osc-percentage">
                                {careHome.open_score.toFixed(1)}
                            </text>
                        </svg>
                    </div>
                    <div className="osc-header-text-container">
                        <div className="osc-text-rate">
                            <p>{updatedText}</p>
                        </div>
                    </div>
                    <div className="osc-action-links">
                        <a 
                            href={`https://www.autumna.co.uk/providers/${careHome.slug}/?utm_source=OpenScoreMap&utm_medium=details`}
                            target="_blank" 
                            //rel="noopener noreferrer"
                            className="osc-action-link"
                            onClick={(e) => {
                                e.stopPropagation();
                                trackExternalLink(`https://www.autumna.co.uk/providers/${careHome.slug}/`, 'details');
                            }}
                        >
                            <div className="osc-action-link-icon">
                                <svg width="38" height="38" viewBox="0 0 24 24" fill="#025A51" xmlns="http://www.w3.org/2000/svg">
                                    <path d="m15.985 17.031c-1.479 1.238-3.384 1.985-5.461 1.985-4.697 0-8.509-3.812-8.509-8.508s3.812-8.508 8.509-8.508c4.695 0 8.508 3.812 8.508 8.508 0 2.078-.747 3.984-1.985 5.461l4.749 4.75c.146.146.219.338.219.531 0 .587-.537.75-.75.75-.192 0-.384-.073-.531-.22zm-5.461-13.53c-3.868 0-7.007 3.14-7.007 7.007s3.139 7.007 7.007 7.007c3.866 0 7.007-3.14 7.007-7.007s-3.141-7.007-7.007-7.007zm.741 8.499h-4.5c-.414 0-.75.336-.75.75s.336.75.75.75h4.5c.414 0 .75-.336.75-.75s-.336-.75-.75-.75zm3-2.5h-7.5c-.414 0-.75.336-.75.75s.336.75.75.75h7.5c.414 0 .75-.336.75-.75s-.336-.75-.75-.75zm0-2.5h-7.5c-.414 0-.75.336-.75.75s.336.75.75.75h7.5c.414 0 .75-.336.75-.75s-.336-.75-.75-.75z" fillRule="nonzero"/>
                                </svg>
                            </div>
                            <span className="osc-action-link-text">Details</span>
                        </a>
                        <a 
                            href={`https://www.autumna.co.uk/providers/contact/${careHome.id}/?utm_source=OpenScoreMap&utm_medium=contact`}
                            target="_blank" 
                            //rel="noopener noreferrer"
                            className="osc-action-link"
                            onClick={(e) => {
                                e.stopPropagation();
                                trackExternalLink(`https://www.autumna.co.uk/providers/contact/${careHome.id}/`, 'contact');
                            }}
                        >
                            <div className="osc-action-link-icon">
                                <svg width="38" height="38" viewBox="0 0 24 24" fill="#025A51" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M14.89 23.654c-7.367 3.367-18.802-18.86-11.601-22.615l2.107-1.039 3.492 6.817-2.082 1.026c-2.189 1.174 2.37 10.08 4.609 8.994.091-.041 2.057-1.007 2.064-1.011l3.521 6.795c-.008.004-1.989.978-2.11 1.033zm-1.538-13.409l2.917.87c.223-.747.16-1.579-.24-2.317-.399-.739-1.062-1.247-1.808-1.469l-.869 2.916zm1.804-6.058c1.551.462 2.926 1.516 3.756 3.051.831 1.536.96 3.263.498 4.813l-1.795-.535c.325-1.091.233-2.306-.352-3.387-.583-1.081-1.551-1.822-2.643-2.146l.536-1.796zm.95-3.187c2.365.705 4.463 2.312 5.729 4.656 1.269 2.343 1.466 4.978.761 7.344l-1.84-.548c.564-1.895.406-4.006-.608-5.882-1.016-1.877-2.696-3.165-4.591-3.729l.549-1.841z"/>
                                </svg>
                            </div>
                            <span className="osc-action-link-text">Contact</span>
                        </a>
                    </div>
                </div>
            </div>
            <div className="osc-body">
                <div className="osc-regulator">
                    <div className="osc-regulator-row">
                        <div className="osc-regulator-column" style={{flexBasis: '70%'}}>Regulator Last Inspection:</div>
                        <div className="osc-regulator-column" style={{flexBasis: '30%'}}>Fee</div>
                    </div>
                    <div className="osc-regulator-row">
                        <div className="osc-regulator-column" style={{flexBasis: '70%'}}>
                            <b>{inspectionTime}</b>
                        </div>
                        <div className="osc-regulator-column" style={{flexBasis: '30%'}}>
                            <span className="osc-relative-fee">{costIndicator}<span className="osc-relative-fee-background">{costIndicatorBackground}</span></span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OpenScoreCard;
